import {
  DEFAULT_COMPANY_LANGUAGE,
  isLanguageISO6391,
  LanguageISO6391,
} from 'ai-constants'

export interface InspectionLanguageSource {
  locale?: string
  template?: {
    language?: unknown
  }
}

const normalizeLocaleLanguage = (locale?: string) => {
  if (!locale) return

  const [language] = locale.toLowerCase().split(/[-_]/)

  if (!language) return

  return language
}

const getAcceptLanguageEntries = (acceptLanguageHeader?: string | string[]) => {
  if (!acceptLanguageHeader) return []

  const acceptLanguageValue = Array.isArray(acceptLanguageHeader)
    ? acceptLanguageHeader.join(',')
    : acceptLanguageHeader

  return acceptLanguageValue
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [localeSegment, ...qualitySegments] = entry.split(';')
      const qualityValue = qualitySegments.find((segment) =>
        segment.trim().startsWith('q='),
      )
      const quality = qualityValue
        ? Number.parseFloat(qualityValue.trim().replace('q=', ''))
        : 1

      return {
        language: normalizeLocaleLanguage(localeSegment),
        quality: Number.isFinite(quality) ? quality : 0,
      }
    })
    .sort((left, right) => right.quality - left.quality)
}

export const resolveInspectionLanguage = (
  inspection?: InspectionLanguageSource | null,
): LanguageISO6391 => {
  if (
    typeof inspection?.template?.language === 'string' &&
    isLanguageISO6391(inspection.template.language)
  )
    return inspection.template.language

  const localeLanguage = normalizeLocaleLanguage(inspection?.locale)

  if (localeLanguage && isLanguageISO6391(localeLanguage)) return localeLanguage

  return DEFAULT_COMPANY_LANGUAGE
}

export const resolveRequestLanguage = (
  acceptLanguageHeader?: string | string[],
): LanguageISO6391 => {
  const matchedEntry = getAcceptLanguageEntries(acceptLanguageHeader).find(
    (entry) => entry.language && isLanguageISO6391(entry.language),
  )

  if (matchedEntry?.language && isLanguageISO6391(matchedEntry.language))
    return matchedEntry.language

  return DEFAULT_COMPANY_LANGUAGE
}

export const resolveAppLanguage = ({
  inspection,
  requestLanguage,
}: {
  inspection?: InspectionLanguageSource | null
  requestLanguage?: LanguageISO6391 | null
}): LanguageISO6391 => {
  if (inspection) return resolveInspectionLanguage(inspection)

  if (requestLanguage && isLanguageISO6391(requestLanguage))
    return requestLanguage

  return DEFAULT_COMPANY_LANGUAGE
}
